import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isEqualWithHTML } from '@/utils/editor-utils';

export const useAutosave = (options: AutoSaveOptions) => {
  const {
    data,
    onSave,
    initialData,
    delay = 500,
    enabled = true,
    resetKey,
    hasContentChanges = false,
    flushOnLeave = false,
  } = options;

  const [isSaving, setIsSaving] = useState(false);
  const [baseline, setBaseline] = useState<AutosaveBaseline>(() => ({
    resetKey,
    hasInitialized: false,
    lastSavedData: initialData,
  }));

  const baselineRef = useRef(baseline);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightSaveRef = useRef<AutosaveInFlight | null>(null);

  const baselineMatchesResetKey = Object.is(baseline.resetKey, resetKey);
  const hasInitialized = baselineMatchesResetKey ? baseline.hasInitialized : false;
  const lastSavedData = baselineMatchesResetKey ? baseline.lastSavedData : initialData;

  /**
   * Refs are used to keep the latest `data` and `onSave` without making them dependencies of the autosave timer effect.
   * If we depended directly on `data` or `onSave`, frequent re-renders (for example while streaming editor updates) would constantly recreate
   * those values and reset the debounce timer before it fires.
   * Using refs allows the timer to stay stable while still calling the latest `onSave` with the most recent `data`.
   * We intentionally do not rely on `useCallback` here because the callback may depend on frequently changing values (like editor content), which
   * would recreate the function and restart the debounce cycle.
   */
  const pendingDataRef = useRef(data);
  // Read through a ref because saves are triggered from timers and `flush`, long after the render
  // that observed the change.
  const hasContentChangesRef = useRef(hasContentChanges);
  const initialDataRef = useRef(initialData);
  const saveCallbackRef = useRef(onSave);
  const enabledRef = useRef(enabled);

  const applyBaseline = useCallback((nextBaseline: AutosaveBaseline) => {
    baselineRef.current = nextBaseline;
    setBaseline(nextBaseline);
  }, []);

  const clearDebounceTimer = useCallback(() => {
    if (!debounceTimerRef.current) return;

    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
  }, []);

  const getInitializedBaseline = useCallback(() => {
    const currentBaseline = baselineRef.current;

    if (Object.is(currentBaseline.resetKey, resetKey) && currentBaseline.hasInitialized) {
      return currentBaseline;
    }

    const initializedBaseline = {
      resetKey,
      hasInitialized: true,
      lastSavedData: initialDataRef.current,
    };

    applyBaseline(initializedBaseline);

    return initializedBaseline;
  }, [applyBaseline, resetKey]);

  const savePendingData = useCallback(async (): Promise<AutosaveFlushResult> => {
    const currentInFlightSave = inFlightSaveRef.current;

    if (currentInFlightSave && Object.is(currentInFlightSave.resetKey, resetKey)) {
      return currentInFlightSave.promise;
    }

    const currentBaseline = getInitializedBaseline();
    const pendingData = pendingDataRef.current;

    if (!hasContentChangesRef.current && isEqualWithHTML(pendingData, currentBaseline.lastSavedData)) {
      return { didSave: false };
    }

    setIsSaving(true);

    const savePromise = (async () => {
      await saveCallbackRef.current(pendingData);

      if (!Object.is(baselineRef.current.resetKey, resetKey)) {
        return { didSave: true };
      }

      applyBaseline({
        resetKey,
        hasInitialized: true,
        lastSavedData: pendingData,
      });

      return { didSave: true };
    })();

    let trackedSave: Promise<AutosaveFlushResult>;
    trackedSave = savePromise.finally(() => {
      if (inFlightSaveRef.current?.promise === trackedSave) {
        inFlightSaveRef.current = null;
        setIsSaving(false);
      }
    });

    inFlightSaveRef.current = { resetKey, promise: trackedSave };

    return trackedSave;
  }, [applyBaseline, getInitializedBaseline, resetKey]);

  /**
   * A fresh save after whatever is in flight. `savePendingData` alone would hand back the in-flight
   * promise, whose snapshot predates anything the host reported since it started. Starts
   * synchronously when nothing is in flight: an unloading page gets no later turn.
   */
  const saveAfterInFlight = useCallback((): Promise<AutosaveFlushResult> => {
    const inFlightSave = inFlightSaveRef.current?.promise;
    return inFlightSave ? inFlightSave.then(savePendingData, savePendingData) : savePendingData();
  }, [savePendingData]);

  // Read through a ref for the same reason as `onSave`: the leave save runs from an unmount cleanup
  // and page events, and must use the version belonging to the last committed render.
  const saveAfterInFlightRef = useRef(saveAfterInFlight);

  useEffect(() => {
    pendingDataRef.current = data;
  }, [data]);

  useEffect(() => {
    hasContentChangesRef.current = hasContentChanges;
  }, [hasContentChanges]);

  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  useEffect(() => {
    saveCallbackRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    saveAfterInFlightRef.current = saveAfterInFlight;
  }, [saveAfterInFlight]);

  useEffect(() => {
    if (!enabled || hasInitialized) return;

    applyBaseline({
      resetKey,
      hasInitialized: true,
      lastSavedData: initialData,
    });
  }, [applyBaseline, enabled, hasInitialized, initialData, resetKey]);

  const hasChanges = useMemo(
    () => enabled && hasInitialized && (hasContentChanges || !isEqualWithHTML(data, lastSavedData)),
    [data, enabled, hasContentChanges, hasInitialized, lastSavedData]
  );

  const flush = useCallback(() => {
    clearDebounceTimer();

    return (async () => {
      let latestSaveResult: AutosaveFlushResult = { didSave: false };

      let saveResult = await saveAfterInFlight();

      while (true) {
        if (saveResult.didSave) {
          latestSaveResult = saveResult;
        }

        const currentBaseline = baselineRef.current;

        if (!Object.is(currentBaseline.resetKey, resetKey) || !currentBaseline.hasInitialized) {
          return latestSaveResult;
        }

        // Only `data` that moved during the save warrants another pass. The content flag is read
        // through a ref that only a re-render can clear, so looping on it could never terminate;
        // the fresh save above already covered whatever content the host reported.
        if (isEqualWithHTML(pendingDataRef.current, currentBaseline.lastSavedData)) {
          return latestSaveResult;
        }

        saveResult = await savePendingData();
      }
    })();
  }, [clearDebounceTimer, resetKey, saveAfterInFlight, savePendingData]);

  useEffect(() => {
    if (!hasChanges) return;

    const handler = setTimeout(() => {
      debounceTimerRef.current = null;
      runSaveInBackground(savePendingData);
    }, delay);
    debounceTimerRef.current = handler;

    return () => {
      if (debounceTimerRef.current === handler) {
        debounceTimerRef.current = null;
      }

      clearTimeout(handler);
    };
  }, [delay, hasChanges, lastSavedData, savePendingData]);

  /** One save covers everything for a host that is leaving, since it takes no further edits. */
  const saveOnLeave = useCallback(() => {
    if (!enabledRef.current) return;

    clearDebounceTimer();
    runSaveInBackground(saveAfterInFlightRef.current);
  }, [clearDebounceTimer]);

  useEffect(() => {
    if (!flushOnLeave) return;

    // Best-effort: nothing guarantees an asynchronous write completes once the page is going away.
    // `visibilitychange` fires first on tab close and also covers backgrounding, where it does complete.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveOnLeave();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', saveOnLeave);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', saveOnLeave);

      saveOnLeave();
    };
  }, [flushOnLeave, saveOnLeave]);

  return { isSaving, hasChanges, flush };
};

const runSaveInBackground = (save: () => Promise<AutosaveFlushResult>) => {
  save().catch((error) => {
    console.error('Autosave failed:', error);
  });
};

interface AutoSaveOptions {
  delay?: number;
  enabled?: boolean;
  resetKey?: unknown;
  data: Record<string, any>;
  initialData: Record<string, any>;
  onSave: (data: Record<string, any>) => Promise<any>;
  /**
   * Set by hosts whose content is too large to diff here — an editor reports whether it holds
   * unsaved content directly, instead of putting the document in `data`. It is a live answer rather
   * than something compared against the snapshot taken when a save started, which is what makes it
   * safe: a save completing, an edit undone, or a re-mount cannot make it disagree with itself.
   */
  hasContentChanges?: boolean;
  /** Saves pending data when the host unmounts or the page is hidden or unloaded, instead of dropping it. */
  flushOnLeave?: boolean;
}

type AutosaveBaseline = {
  resetKey: unknown;
  hasInitialized: boolean;
  lastSavedData: Record<string, any>;
};

type AutosaveFlushResult = {
  didSave: boolean;
};

type AutosaveInFlight = {
  resetKey: unknown;
  promise: Promise<AutosaveFlushResult>;
};
